import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchSalesOrderDetail } from '#/api/crmMgmt/salesOrder/fetchDetail';
import { fetchSalesOrderInventoryCandidates } from '#/api/crmMgmt/salesOrder/fetchInventoryCandidates';
import { updateSalesOrder } from '#/api/crmMgmt/salesOrder/update';
import { updateSalesOrderLine } from '#/api/crmMgmt/salesOrder/updateLine';
import { ROUTES } from '#/config/routes';
import { Button } from '#/modules/commonComponents/button';
import SalesOrderAllocationEditor from '#/modules/crmMgmt/salesOrder/components/SalesOrderAllocationEditor';
import SalesOrderEditReferenceSections from '#/modules/crmMgmt/salesOrder/components/SalesOrderEditReferenceSections';
import SalesOrderStatusBadge from '#/modules/crmMgmt/salesOrder/components/SalesOrderStatusBadge';
import {
  getSalesOrderApiErrorMessage,
  isSalesOrderVersionConflict,
} from '#/modules/crmMgmt/salesOrder/helper/apiError';
import {
  buildSalesOrderLineAllocationsPayload,
  buildSalesOrderWritePayload,
} from '#/modules/crmMgmt/salesOrder/helper/buildSalesOrderWritePayload';
import { mapSalesOrderDetailToForm } from '#/modules/crmMgmt/salesOrder/helper/mapSalesOrderDetailToForm';
import { findDuplicateInventoryStockId } from '#/modules/crmMgmt/salesOrder/helper/salesOrderAllocationHelpers';
import {
  canAllocateSalesOrderInventory,
  SALES_ORDER_STATUS,
} from '#/modules/crmMgmt/salesOrder/helper/salesOrderStatus';
import {
  SALES_ORDER_FORM_DEFAULT_VALUES,
  salesOrderFormSchema,
} from '#/modules/crmMgmt/salesOrder/schemas/salesOrderSchema';
import CreateEditReferenceRail from '#/modules/infra/layout/CreateEditReferenceRail';
import PageShell from '#/modules/infra/layout/PageShell';
import {
  salesOrderDetailQueryKey,
  salesOrderInventoryCandidatesQueryKey,
  salesOrderListQueryKeyPrefix,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

const SalesOrderAllocatePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const updatedAtRef = useRef(null);
  const [isPending, setIsPending] = useState(false);

  const detailQuery = useQuery({
    queryKey: salesOrderDetailQueryKey(id),
    queryFn: () => fetchSalesOrderDetail(id),
    enabled: Boolean(id),
  });

  const candidatesQuery = useQuery({
    queryKey: salesOrderInventoryCandidatesQueryKey(id),
    queryFn: () => fetchSalesOrderInventoryCandidates(id),
    enabled: Boolean(id) && canAllocateSalesOrderInventory(detailQuery.data?.data?.status),
  });

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
  } = useForm({
    resolver: zodResolver(salesOrderFormSchema),
    defaultValues: SALES_ORDER_FORM_DEFAULT_VALUES,
  });

  const detail = detailQuery.data?.data;
  const isPageLoading = Boolean(id) && detailQuery.isLoading;
  const isDetailNotFound = Boolean(id) && detailQuery.isError;
  const isReadOnlyAllocate = Boolean(detail) && !canAllocateSalesOrderInventory(detail.status);
  const canShowAllocate = Boolean(detail)
    && !isPageLoading
    && !isDetailNotFound
    && !isReadOnlyAllocate;

  useEffect(() => {
    if (!detail) {
      return;
    }

    if (!canAllocateSalesOrderInventory(detail.status)) {
      toast.warning('僅草稿／備貨中可配置庫存，已返回詳情');
      navigate(ROUTES.salesOrderDetail(id), { replace: true });
    }
  }, [detail, id, navigate]);

  useEffect(() => {
    if (!detail) {
      return;
    }

    updatedAtRef.current = detail.record?.updatedAt ?? detail.updatedAt ?? null;
    reset(mapSalesOrderDetailToForm(detail));
  }, [detail, reset]);

  const saveMutation = useMutation({
    mutationFn: async (values) => {
      const status = detail?.status;
      let updatedAt = updatedAtRef.current;

      if (status === SALES_ORDER_STATUS.PREPARING) {
        let lastResult = null;
        for (const line of values?.lines ?? []) {
          if (!line?.id) {
            continue;
          }
          lastResult = await updateSalesOrderLine(
            id,
            line.id,
            buildSalesOrderLineAllocationsPayload(line, { updatedAt }),
          );
          updatedAt = lastResult?.data?.record?.updatedAt
            ?? lastResult?.data?.updatedAt
            ?? updatedAt;
          updatedAtRef.current = updatedAt;
        }
        return lastResult;
      }

      return updateSalesOrder(
        id,
        buildSalesOrderWritePayload(values, { updatedAt }),
      );
    },
  });

  async function onSubmit(values) {
    const duplicateId = findDuplicateInventoryStockId(values?.lines);
    if (duplicateId) {
      toast.error('整張銷貨單不可重複配置同一庫存');
      return;
    }

    setIsPending(true);
    try {
      const result = await saveMutation.mutateAsync(values);
      toast.success(result?.message || '庫存配置已儲存');
      await queryClient.invalidateQueries({
        queryKey: salesOrderListQueryKeyPrefix(),
      });
      await queryClient.invalidateQueries({
        queryKey: salesOrderDetailQueryKey(id),
      });
      await queryClient.invalidateQueries({
        queryKey: salesOrderInventoryCandidatesQueryKey(id),
      });
      navigate(ROUTES.salesOrderDetail(id));
    } catch (error) {
      if (isSalesOrderVersionConflict(error)) {
        toast.error(getSalesOrderApiErrorMessage(error, '版本衝突，請重新載入'));
        detailQuery.refetch();
        candidatesQuery.refetch();
        return;
      }
      toast.error(getSalesOrderApiErrorMessage(error, '儲存失敗'));
    } finally {
      setIsPending(false);
    }
  }

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="flex min-h-0 w-full flex-1 flex-col">
          <div className="create_edit_page__header">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="page_title mb-0! flex flex-wrap items-center gap-2">
                <span className="border-l-5 border-primary-400 pl-2.5">配置庫存</span>
                {detail?.status ? (
                  <SalesOrderStatusBadge status={detail.status} size="sm" />
                ) : null}
              </h1>
              <Button type="button" variant="outline" asChild>
                <Link to={ROUTES.salesOrderDetail(id)}>返回詳情</Link>
              </Button>
              {detail?.customerOrderId ? (
                <Button type="button" variant="outline" asChild>
                  <Link to={ROUTES.customerOrderDetail(detail.customerOrderId)}>
                    返回客訂
                  </Link>
                </Button>
              ) : null}
            </div>
          </div>

          {isPageLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {isDetailNotFound ? (
            <p className="text-sm text-destructive">
              {getSalesOrderApiErrorMessage(detailQuery.error, '找不到銷貨單')}
            </p>
          ) : null}

          {canShowAllocate ? (
            <CreateEditReferenceRail
              className="min-h-0 flex-1"
              mainClassName="mx-auto w-full max-w-6xl lg:mr-auto"
              main={(
                <form
                  className="sheet create_edit create_edit_form create_edit_form--fill"
                  noValidate
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <div className="create_edit_form__body">
                    <SalesOrderAllocationEditor
                      control={control}
                      getValues={getValues}
                      setValue={setValue}
                      candidatesData={candidatesQuery.data?.data}
                      candidatesLoading={candidatesQuery.isLoading}
                      candidatesError={candidatesQuery.error}
                    />
                  </div>

                  <div className="create_edit_form__actions flex flex-wrap justify-end gap-2">
                    <Button type="button" variant="outline" asChild>
                      <Link to={ROUTES.salesOrderDetail(id)}>取消</Link>
                    </Button>
                    <Button type="submit" disabled={isPending}>
                      {isPending ? '儲存中…' : '儲存'}
                    </Button>
                  </div>
                </form>
              )}
              reference={<SalesOrderEditReferenceSections detail={detail} />}
            />
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default SalesOrderAllocatePage;
